import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { LoggingService } from '@com-delta-idp/mnx-utils';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, share } from 'rxjs/operators';
import { CookieHelper } from '../../common/cookie-helper';
import { ApplicationConfig } from '../../config/config';
import { UrlUtil } from '../../config/urlutil';
import { HTTP_TIMEOUT } from '../../interceptor/timeout.interceptor';
import { AcctactvtyTracker } from '../../model/acctactvty-tracker.model';
import {
  MultiCityResponse,
  RecentData
} from '../../model/mc-response.interface';
import { CommonService } from '../common.service';

@Injectable({
  providedIn: 'root'
})
export class TrackerDataService {
  acctTracker: AcctactvtyTracker;
  private recentSearchData: any;
  private savedData: any;
  private errorData: any;
  private recentSearchObservable: Observable<any>;
  private recentSearchMulticityObservable: Observable<any>;
  private observableError: Observable<any>;
  private refreshSessionObservable: Observable<any>;
  private trackerDataObservable: Observable<any>;
  private observable: Observable<any>;
  private sessionData: any;
  private recentSearchesLoaded = false;
  private savedSearchesLoaded = false;
  data = null;
  recentData: RecentData;
  multiCityResponse: MultiCityResponse;
  constructor(
    private http: HttpClient,
    private config: ApplicationConfig,
    private urlUtil: UrlUtil,
    private srv: CommonService,
    private cookieHelper: CookieHelper,
    private loggingService: LoggingService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  public getTrackerData(skymilesNbr): Observable<any> {
    const nmbrOfTrips = 1;
    const accountTypeCookieVal =
      this.cookieHelper.getCookieValue('accountType');
    if (
      accountTypeCookieVal &&
      accountTypeCookieVal !== '' &&
      accountTypeCookieVal !== null
    ) {
      if (this.data) {
        return of(this.data);
      } else if (this.trackerDataObservable) {
        return this.trackerDataObservable;
      } else if (isPlatformBrowser(this.platformId)) {
        this.trackerDataObservable = this.callTrackerService(skymilesNbr).pipe(
          map(
            (response) => {
              this.trackerDataObservable = null;
              if (response) {
                this.data = response;
              } else {
                this.data = null;
              }
              return this.data;
            },
            (err) => {
              this.loggingService.log(
                'Caught Error in trackeer service call: ' + err
              );
            }
          ),
          share()
        );
        return this.trackerDataObservable;
      }
    } else {
      const error = new Error('User session invalid: Please login to continue');
      return throwError(error).pipe(share());
    }
  }

  callTrackerService(skymilesNbr: string) {
    const svcTimeOut: number = Number.parseInt(
      this.config.get('APP_TIMEOUT_SLOW')
    );
    const customheaders = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });
    customheaders.append('Accept', 'application/json');
    customheaders.append(HTTP_TIMEOUT, svcTimeOut.toString());
    return this.http.post(
      `${this.config.get('APP_SESS_CUSTOMER_TRACKER_URL')}`,
      this.urlUtil.toObjQueryString({ skymilesNbr: skymilesNbr }),
      { headers: customheaders }
    );
  }

  public getRecentSearches(): Observable<any> {
    const svcTimeOut: number = Number.parseInt(
      this.config.get('APP_TIMEOUT_FAST')
    );
    const customheaders = new HttpHeaders({
      'Content-Type': 'application/json; charset=utf-8'
    });
    customheaders.append('Accept', 'application/json');
    customheaders.append(HTTP_TIMEOUT, svcTimeOut.toString());
    if (this.recentSearchData) {
      return of(this.recentSearchData);
    } else if (this.recentSearchObservable) {
      return this.recentSearchObservable;
    } else {
      this.recentSearchObservable = this.http
        .get(
          `${this.config.get(
            'APP_SESS_CUSTOMER_GETPREFILL_URL'
          )}&cacheBustParam=${new Date().getTime()}`,
          { headers: customheaders }
        )
        .pipe(
          map((response) => {
            this.recentSearchObservable = null;
            this.recentSearchData = response;
            return this.recentSearchData;
          }),
          share()
        );
      return this.recentSearchObservable;
    }
  }

  postRecentSearches(recentSearchData: RecentData): Observable<any> {
    const svcTimeOut: number = Number.parseInt(
      this.config.get('APP_TIMEOUT_FAST')
    );
    const customheaders = new HttpHeaders({
      'Content-Type': 'application/json; charset=utf-8'
    });
    customheaders.append('Accept', 'application/json');
    customheaders.append(HTTP_TIMEOUT, svcTimeOut.toString());
    const customParams = new HttpParams().set(
      'searchType',
      'RecentSearchesJSON'
    );
    return this.http
      .post(
        `${this.config.get('APP_SESS_CUSTOMER_SAVEPREFILL_URL')}`,
        recentSearchData,
        { headers: customheaders, params: customParams }
      )
      .pipe(
        catchError((err) => this._handlePostSearchError(err)),
        share()
      );
  }

  public getSavedSearches(customerId): Observable<any> {
    const svcTimeOut: number = Number.parseInt(
      this.config.get('APP_TIMEOUT_FAST')
    );
    const customheaders = {
      Accept: 'application/json;charset=UTF-8',
      appId: 'cus',
      channelId: 'ecom'
    };
    // customheaders.append(HTTP_TIMEOUT, svcTimeOut.toString());
    if (this.savedData) {
      return of(this.savedData);
    } else if (this.observable) {
      return this.observable;
    } else {
      this.observable = this.http
        .get(`${this.config.get('APP_SESS_CUSTOMER_SAVEDSEARCH_URL')}`, {
          headers: customheaders
        })
        .pipe(
          map((res) => {
            this.observable = null;
            this.savedData = res;
            return this.savedData;
          }),
          share()
        );
      return this.observable;
    }
  }

  public refreshSession(): Observable<any> {
    const customheaders = new HttpHeaders({
      'Content-Type': 'application/json; charset=utf-8'
    });
    customheaders.append('Accept', 'application/json');

    return this.http
      .post(`${this.config.get('APP_SESS_REFRESH_SESSION_URL')}`, {
        headers: customheaders
      })
      .pipe(share());
  }

  public refreshSessionDashboard(): Observable<any> {
    if (this.refreshSessionObservable) {
      return this.refreshSessionObservable;
    } else {
      this.refreshSessionObservable = this.srv.getSessCustData().pipe(share());
      return this.refreshSessionObservable;
    }
  }

  public setRecentSearchesLoaded(isLoaded: boolean) {
    this.recentSearchesLoaded = isLoaded;
  }

  public getRecentSearchesLoaded(): boolean {
    return this.recentSearchesLoaded;
  }

  public setSavedSearchesLoaded(isLoaded: boolean) {
    this.savedSearchesLoaded = isLoaded;
  }

  public getSavedSearchesLoaded(): boolean {
    return this.savedSearchesLoaded;
  }

  /**
   * This method will post the current multicities selection
   *
   * @returns observable of post MC
   * @memberof TrackerDataService
   */
  postMultiCitiesSearches(
    multiCityResponse: MultiCityResponse
  ): Observable<any> {
    const customheaders = new HttpHeaders({
      'Content-Type': 'application/json; charset=utf-8'
    });
    customheaders.append('Accept', 'application/json');

    const url = `${this.config.get('APP_SESS_CUSTOMER_EDP_DATA_SLIM_NAV_URL')}`;

    return this.http
      .post(url, multiCityResponse, {
        headers: customheaders,
        responseType: 'json'
      })
      .pipe(
        catchError((err) => this._handlePostSearchError(err)),
        share()
      );
  }

  /**
   * This method will perfomr and return the most recent searches related to multicity
   *
   * @memberof TrackerDataService
   */
  getMultiCityRecentSearches(mc_cache_key: string) {
    const svcTimeOut: number = Number.parseInt(
      this.config.get('APP_TIMEOUT_FAST')
    );
    const customheaders = new HttpHeaders({
      'Content-Type': 'application/json; charset=utf-8'
    });
    customheaders.append('Accept', 'application/json');
    customheaders.append(HTTP_TIMEOUT, svcTimeOut.toString());
    const customParams = new HttpParams().set('CacheKey', mc_cache_key);

    if (this.recentSearchData) {
      return of(this.recentSearchData);
    } else if (this.recentSearchMulticityObservable) {
      return this.recentSearchMulticityObservable;
    } else {
      this.recentSearchMulticityObservable = this.http
        .get(
          `${this.config.get('APP_SESS_CUSTOMER_EDP_RETRIEVE_SLIM_NAV_URL')}`,
          { headers: customheaders, params: customParams }
        )
        .pipe(
          map((response) => {
            this.recentSearchMulticityObservable = null;
            this.recentSearchData = response;
            return this.recentSearchData;
          }),
          catchError((error) => {
            this.recentSearchMulticityObservable = null;
            if (error.status === 404) {
              this.cookieHelper.deleteCookie('mc_cache_key');
              this.cookieHelper.setCookies('trip_type', '');
              return of(null);
            }
            this.loggingService.log(
              'Caught Error in getMultiCityRecentSearches: ' + error
            );
          }),
          share()
        );
      return this.recentSearchMulticityObservable;
    }
  }

  /**
   * This method will perfomr and return the uncached recent searches related to multicity
   * Every call to this method should result in a new http call to retrieve multicity data
   * Created as an alternative to getMultiCityRecentSearches
   * getMultiCityRecentSearches shares recentSearchObservable and other variables and sometimes
   * returns recent searches results (if already cached) instead of multicity data
   *
   * @memberof TrackerDataService
   */
  getUncachedMultiCityRecentSearches(mc_cache_key: string) {
    console.log(
      '%c getUncachedMultiCityRecentSearches called',
      'color: yellow'
    );
    const svcTimeOut: number = Number.parseInt(
      this.config.get('APP_TIMEOUT_FAST')
    );
    const customheaders = new HttpHeaders({
      'Content-Type': 'application/json; charset=utf-8'
    });
    customheaders.append('Accept', 'application/json');
    customheaders.append(HTTP_TIMEOUT, svcTimeOut.toString());
    const customParams = new HttpParams().set('CacheKey', mc_cache_key);
    return this.http
      .get(
        `${this.config.get('APP_SESS_CUSTOMER_EDP_RETRIEVE_SLIM_NAV_URL')}`,
        { headers: customheaders, params: customParams }
      )
      .pipe(
        map((response) => {
          console.log(
            '%c trackerData.service getUncachedMultiCityRecentSearches response is below',
            'color: yellow'
          );
          console.log(response);
          return response;
        }),
        catchError((error) => {
          if (error.status === 404) {
            this.cookieHelper.deleteCookie('mc_cache_key');
            this.cookieHelper.setCookies('trip_type', '');
            return of(null);
          }
          this.loggingService.log(
            'Caught Error in getUncachedMultiCityRecentSearches: ' + error
          );
        }),
        share()
      );
  }

  getDeepLinkSlimNav(deepLinkCacheKey: string, deepLinkXssid: string) {
    console.log('%c getDeepLinlSlimNav called', 'color: yellow');
    const svcTimeOut: number = Number.parseInt(
      this.config.get('APP_TIMEOUT_FAST')
    );
    const customheaders = new HttpHeaders({
      'Content-Type': 'application/json',
      Accept: 'application/json'
    });
    customheaders.append(HTTP_TIMEOUT, svcTimeOut.toString());
    const data = {
      CacheKey: deepLinkCacheKey,
      deeepLinkXssid: deepLinkXssid
    };
    const customParams = new HttpParams({ fromObject: data });
    return this.http
      .get(this.config.get('APP_RETRIEVE_SLIM_NAV_URL'), {
        headers: customheaders,
        params: customParams
      })
      .toPromise();
  }

  getDeepLinkValue() {
    console.log('%c getDeepLinlSlimNav called', 'color: yellow');
    const svcTimeOut: number = Number.parseInt(
      this.config.get('APP_TIMEOUT_FAST')
    );
    const customheaders = new HttpHeaders({
      'Content-Type': 'application/json',
      Accept: 'application/json'
    });
    customheaders.append(HTTP_TIMEOUT, svcTimeOut.toString());
    return this.http
      .get(this.config.get('APP_GET_ALL_COOKIE'), { headers: customheaders })
      .toPromise();
  }

  async getRecentSearchForDeepLink() {
    console.log('%c getRecentSearchForDeepLink called', 'color: red');
    const svcTimeOut: number = Number.parseInt(
      this.config.get('APP_TIMEOUT_FAST')
    );
    const customheaders = new HttpHeaders({
      'Content-Type': 'application/json',
      Accept: 'application/json'
    });
    customheaders.append(HTTP_TIMEOUT, svcTimeOut.toString());
    return await this.http
      .get(
        `${this.config.get(
          'APP_SESS_CUSTOMER_GETPREFILL_URL'
        )}&cacheBustParam=${new Date().getTime()}`,
        { headers: customheaders }
      )
      .toPromise();
  }

  public async postRecentSearchesForDeepLink(recentSearchData) {
    const svcTimeOut: number = Number.parseInt(
      this.config.get('APP_TIMEOUT_FAST')
    );
    const customheaders = new HttpHeaders({
      'Content-Type': 'application/json; charset=utf-8'
    });
    customheaders.append('Accept', 'application/json');
    customheaders.append(HTTP_TIMEOUT, svcTimeOut.toString());
    const customParams = new HttpParams().set(
      'searchType',
      'RecentSearchesJSON'
    );
    return await this.http
      .post(
        `${this.config.get('APP_SESS_CUSTOMER_SAVEPREFILL_URL')}`,
        recentSearchData,
        { headers: customheaders, params: customParams }
      )
      .toPromise();
  }

  private handleError(error: Response) {
    return Observable.throw(error.statusText);
  }

  private _handlePostSearchError(
    err: HttpErrorResponse
  ): Observable<HttpErrorResponse> {
    this.loggingService.log(`Caught Error: ${err.message}`);
    return of(err); // used of operator instead of throwerror operator why because we are using forkjoin so if incase prefill/updateSearch call fails it's going inside error block so can't able to reach flight results page
  }
}
